import React, { useCallback } from 'react'
import { css, SerializedStyles } from '@emotion/core'

type Props = {
  canEdit?: boolean
  isEditing?: boolean
  onStartEdit?: () => void
  openOnClick?: boolean
  editButtonAriaLabel: string
  children: React.ReactNode
  customStyle?: SerializedStyles
}

export const RestaurantEditableCellShell: React.FC<Props> = ({
  canEdit = true,
  isEditing = false,
  onStartEdit,
  openOnClick = false,
  editButtonAriaLabel,
  children,
  customStyle,
}) => {
  const handleContainerClick = useCallback(() => {
    if (!canEdit || isEditing || !openOnClick) {
      return
    }

    onStartEdit?.()
  }, [canEdit, isEditing, onStartEdit, openOnClick])

  const handleContainerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!canEdit || isEditing || !openOnClick) {
        return
      }

      if (event.key !== 'Enter' && event.key !== ' ') {
        return
      }

      event.preventDefault()
      onStartEdit?.()
    },
    [canEdit, isEditing, onStartEdit, openOnClick],
  )

  return (
    <div
      css={[
        shellStyle,
        canEdit && hoverableShellStyle,
        canEdit && focusableShellStyle,
        canEdit && openOnClick && clickableShellStyle,
        customStyle,
      ]}
      tabIndex={canEdit ? 0 : undefined}
      onClick={handleContainerClick}
      onKeyDown={handleContainerKeyDown}
    >
      {children}
      {canEdit && onStartEdit && !isEditing ? (
        <button
          type="button"
          className="restaurant-editable-cell__button"
          css={editButtonStyle}
          aria-label={editButtonAriaLabel}
          onClick={event => {
            event.stopPropagation()
            onStartEdit()
          }}
        >
          <img src={require('@/static/images/edit_no_margin.svg')} alt="" css={editIconStyle} />
        </button>
      ) : null}
    </div>
  )
}

const shellStyle = css({
  minWidth: 0,
  position: 'relative',
  transition: 'background-color 0.16s ease',
  width: '100%',
  '&:focus': {
    outline: 'none',
  },
})

const hoverableShellStyle = css({
  '&:hover': {
    background: '#F5F5F5',
  },
  '&:hover .restaurant-editable-cell__button': {
    opacity: 1,
    pointerEvents: 'auto',
  },
})

const focusableShellStyle = css({
  borderRadius: 16,
})

const clickableShellStyle = css({
  cursor: 'pointer',
})

const editButtonStyle = css({
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  opacity: 0,
  padding: 0,
  pointerEvents: 'none',
  position: 'absolute',
  right: 0,
  top: 0,
  transition: 'opacity 0.16s ease',
  zIndex: 1,
})

const editIconStyle = css({
  width: 16,
})
